import React from 'react';
import { Divider, ConfigProvider } from 'antd';
import type { DividerProps as AntDividerProps } from 'antd';
import type { BaseComponentProps } from '../../types/common';

/**
 * AndDivider Props 接口
 * 基于 Ant Design Divider 组件，完整支持所有 API
 * 
 * API 参考: https://ant.design/components/divider-cn
 * 
 * Props:
 * - children: ReactNode - 分割线内的文字
 * - dashed: boolean - 是否虚线 (已废弃，请使用 variant)
 * - orientation: 'left' | 'right' | 'center' - 分割线标题的位置 (已废弃, 使用 titlePlacement)
 * - titlePlacement: 'start' | 'end' | 'center' - 分割线标题的位置 (5.25.0+)
 * - plain: boolean - 使用轻量级分割文字样式
 * - type: 'horizontal' | 'vertical' - 水平或垂直分割线
 * - variant: 'solid' | 'dashed' | 'dotted' - 分割线变体 (5.20.0+)
 * 
 * Semantic DOM:
 * - root: 根元素
 * - rail: 分割线条元素
 * - content: 内容元素
 */
export interface AndDividerProps extends AntDividerProps, BaseComponentProps {
  /** 分割线文字 (children 的别名，便于 GrapesJS 配置) */
  text?: React.ReactNode;
  /** Semantic DOM 样式 */
  styles?: {
    root?: React.CSSProperties;
    rail?: React.CSSProperties;
    content?: React.CSSProperties;
  };
  /** Design Token */
  token?: Record<string, any>;
}

/**
 * AndDivider 组件
 * 封装 Ant Design Divider，支持完整 API、Semantic DOM 和 Design Token
 */
export const AndDivider: React.FC<AndDividerProps> = ({
  id,
  sessionId,
  develop,
  text,
  children,
  styles,
  token,
  ...restProps
}) => {
  // 渲染内容：优先使用 children，否则使用 text
  const content = children || text;

  // 如果有 Design Token，使用 ConfigProvider 包裹
  if (token && Object.keys(token).length > 0) {
    return (
      <ConfigProvider
        theme={{
          components: {
            Divider: token,
          },
        }}
      >
        <Divider {...restProps} styles={styles}>
          {content}
        </Divider>
      </ConfigProvider>
    );
  }

  return (
    <Divider {...restProps} styles={styles}>
      {content}
    </Divider>
  );
};

export default AndDivider;
